import React, { useEffect, useMemo, useState } from "react";
import styled from "./style.module.css";
import { DashboardCardData, deltaPct } from "./data";
import TopBar from "./parts/TopBar";
import AlertStrip from "./parts/AlertStrip";
import StickyNav from "./parts/StickyNav";
import HeroSummary from "./parts/HeroSummary";
import ActivityFeed from "./parts/ActivityFeed";
import KpiCard from "./parts/KpiCard";
import GrowthChart from "./parts/GrowthChart";
import BreakdownDonut from "./parts/BreakdownDonut";
import BreakdownBars from "./parts/BreakdownBars";
import RankedList from "./parts/RankedList";
import StackedBar from "./parts/StackedBar";
import GroupedBars from "./parts/GroupedBars";
import FunnelChart from "./parts/FunnelChart";
import RadialGauge from "./parts/RadialGauge";
import LeaderboardTable from "./parts/LeaderboardTable";
import ListSection from "./parts/ListSection";
import { COPPER, TEAL, GREEN, MAGENTA } from "./parts/theme";

// A labeled section divider for visual grouping (id doubles as a scroll anchor).
const SectionHeader = ({ accent, title, id }) => (
  <div id={id} className={styled.sectionHeader}>
    <span className={styled.sectionDot} style={{ background: accent }} />
    <h2 className={styled.sectionHeaderTitle}>{title}</h2>
  </div>
);

const money = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Seconds -> "Xm Ys" / "Ys".
const duration = (s) => {
  const sec = Number(s) || 0;
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};

// Pull a single card's display value out of the grouped data by name.
const findCard = (groups, name) => {
  for (const g of groups) {
    const c = (g.cards || []).find((x) => x.name === name);
    if (c) return c.display;
  }
  return "—";
};

const CommonCard = () => {
  const [groups, setGroups] = useState([]);
  const [lists, setLists] = useState({});
  const [trend, setTrend] = useState([]);
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(12); // months, or "all"

  useEffect(() => {
    const fetchData = async () => {
      const { groups = [], lists = {}, trend = [], insights = {} } = await DashboardCardData();
      setGroups(groups);
      setLists(lists);
      setTrend(trend);
      setInsights(insights);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Reslice the (only real) time-series by the selected period; everything else is point-in-time.
  const visibleTrend = useMemo(
    () => (period === "all" ? trend : trend.slice(-period)),
    [trend, period]
  );

  const sparks = useMemo(
    () => ({
      signups: visibleTrend.map((d) => d.signups),
      mau: visibleTrend.map((d) => d.mau),
      dau: visibleTrend.map((d) => d.dau),
    }),
    [visibleTrend]
  );

  if (loading) {
    return <div className={styled.loading}>Loading dashboard…</div>;
  }

  // Shape insights for charts/lists (point-in-time; rendered only when real data exists).
  const mix = insights.subscription_mix;
  const subscriptionMix = mix
    ? [
        { label: "Active", value: mix.active },
        { label: "Trialing", value: mix.trialing },
        { label: "Free", value: mix.free },
        { label: "Expired", value: mix.expired },
      ].filter((d) => d.value > 0)
    : [];

  const nvr = insights.new_vs_returning;
  const newVsReturning = nvr
    ? [
        { label: "New", value: nvr.new },
        { label: "Returning", value: nvr.returning },
      ].filter((d) => d.value > 0)
    : [];

  const funnel = insights.engagement_funnel;
  const funnelStages = funnel
    ? [
        { label: "Screen Views", value: funnel.screen_views },
        { label: "Plays", value: funnel.plays },
        { label: "Completes", value: funnel.completes },
      ]
    : [];

  const qoe = insights.qoe;
  const session = insights.watch_session;
  const reliability = insights.playback_reliability;
  const hasQuality =
    (qoe && qoe.samples > 0) || (session && session.samples > 0) || (reliability && reliability.starts > 0);

  // TVOD (buy/rent) — exact money from ContentEntitlement.
  const tvod = insights.tvod;
  const hasTvod = tvod && tvod.total_transactions > 0;
  const tvodStatus = tvod
    ? (tvod.by_status || []).map((s) => ({ label: s.status, value: s.revenue }))
    : [];
  // A refunded row's access_type comes back from the API as
  // "buy__superseded_<id>" / "rent__superseded_<id>" (freed up so the buyer
  // can re-purchase) — fold those back into their real "buy"/"rent" bucket
  // instead of showing each one as its own donut slice.
  const tvodByType = tvod
    ? Object.values(
        (tvod.by_type || []).reduce((acc, r) => {
          const type = String(r.access_type || "").startsWith("rent") ? "rent" : "buy";
          const bucket = acc[type] || { access_type: type, revenue: 0 };
          bucket.revenue += Number(r.revenue) || 0;
          acc[type] = bucket;
          return acc;
        }, {})
      )
    : [];

  // Admin overview — alerts, library health, activity feed.
  const overview = insights.admin_overview || {};
  const library = overview.library;
  // Drop any creator-payout alerts the backend still sends — not shown on this dashboard.
  const alerts = (overview.alerts || []).filter(
    (a) => !/creator|payout/i.test(a.text || "")
  );

  // Hero "30-second view" — total subscription revenue.
  const mrrNum = parseFloat(String(findCard(groups, "MRR")).replace(/[^0-9.]/g, "")) || 0;
  const heroItems = [
    { label: "Total Monthly Revenue", value: money(mrrNum), accent: GREEN },
    { label: "Active Subscribers", value: findCard(groups, "Active Subscribers"), accent: TEAL },
    { label: "Churn Rate", value: findCard(groups, "Churn Rate"), accent: MAGENTA },
  ];

  // Creator monetization — exact figures from affiliate/payout/watch-ledger tables.
  const creator = insights.creator_monetization;
  const hasCreator =
    creator &&
    (creator.commission_count > 0 ||
      (creator.payouts_by_status || []).length > 0 ||
      creator.total_watch_hours > 0 ||
      (creator.top_creators_by_tvod || []).length > 0);
  const payoutStatus = creator
    ? (creator.payouts_by_status || []).map((p) => ({ label: p.status, value: p.amount }))
    : [];

  // In-page nav — only link to sections that actually render.
  const navItems = [
    { id: "overview", label: "Overview" },
    { id: "revenue", label: "Revenue" },
    hasQuality && { id: "quality", label: "Quality" },
    { id: "audience", label: "Audience" },
    { id: "content", label: "Content" },
  ].filter(Boolean);

  return (
    <div className={styled.dashboard}>
      <TopBar period={period} onPeriodChange={setPeriod} />

      <AlertStrip alerts={alerts} />

      <HeroSummary items={heroItems} />

      <StickyNav items={navItems} />

      {/* Hero KPIs — the 3 trend-backed cards get sparklines+deltas; the rest are "as of now". */}
      <div id="overview" className={styled.kpiRow}>
        <KpiCard
          label="New User Signups (Monthly)"
          value={findCard(groups, "New User Signups (Monthly)")}
          accent={COPPER}
          delta={deltaPct(sparks.signups)}
        />
        <KpiCard
          label="Monthly Active Users"
          value={findCard(groups, "Monthly Active Users")}
          accent={TEAL}
          delta={deltaPct(sparks.mau)}
        />
        <KpiCard
          label="Daily Active Users"
          value={findCard(groups, "Daily Active Users")}
          accent={GREEN}
          delta={deltaPct(sparks.dau)}
        />
        <KpiCard label="MRR" value={findCard(groups, "MRR")} accent={GREEN} />
        <KpiCard label="Active Subscribers" value={findCard(groups, "Active Subscribers")} accent={TEAL} />
        <KpiCard label="Churn Rate" value={findCard(groups, "Churn Rate")} accent={MAGENTA} />
      </div>

      <GrowthChart data={visibleTrend} />

      <SectionHeader id="revenue" accent={GREEN} title="Revenue & Subscriptions" />
      {/* Breakdown charts — all point-in-time. */}
      <div className={styled.chartsRow}>
        <BreakdownDonut
          title="Revenue by Plan"
          rows={lists.revenue_by_plan}
          nameKey="plan"
          valueKey="mrr"
          formatValue={money}
          emptyHint="No paid-plan revenue yet."
        />
        <BreakdownDonut
          title="Subscription Mix"
          rows={subscriptionMix}
          nameKey="label"
          valueKey="value"
          formatValue={(v) => Number(v).toLocaleString()}
          emptyHint="No subscription data yet."
        />
      </div>

      {/* TVOD — exact buy/rent revenue from ContentEntitlement. Shown only when real. */}
      {hasTvod && (
        <>
          <SectionHeader id="tvod" accent={COPPER} title="Transactional VOD (Buy & Rent)" />
          <div className={styled.kpiRow}>
            <KpiCard label="TVOD Revenue" value={money(tvod.total_revenue)} accent={GREEN} />
            <KpiCard
              label="TVOD Transactions"
              value={Number(tvod.total_transactions).toLocaleString()}
              accent={COPPER}
            />
            <KpiCard label="Avg Order Value" value={money(tvod.avg_order_value)} accent={TEAL} />
            {tvod.buyers && tvod.buyers.total > 0 && (
              <>
                <KpiCard
                  label="Repeat Buyer Rate"
                  value={`${tvod.buyers.repeat_rate_percent}%`}
                  accent={MAGENTA}
                />
                <KpiCard label="Avg Spend / Buyer" value={money(tvod.buyers.avg_spend_per_buyer)} accent={GREEN} />
              </>
            )}
          </div>
          <div className={styled.chartsRow}>
            <BreakdownDonut
              title="Buy vs Rent"
              rows={tvodByType}
              nameKey="access_type"
              valueKey="revenue"
              formatValue={money}
              emptyHint="No purchases yet."
            />
            <BreakdownDonut
              title="TVOD Revenue by Platform"
              rows={tvod.by_platform}
              nameKey="platform"
              valueKey="revenue"
              formatValue={money}
              emptyHint="No purchases yet."
            />
            <StackedBar
              title="Entitlement Status"
              rows={tvodStatus}
              formatValue={money}
              emptyHint="No entitlements yet."
            />
          </div>
          <div className={styled.chartsRow}>
            <BreakdownBars
              title="Top Earning Titles (TVOD)"
              rows={tvod.top_titles}
              nameKey="title"
              valueKey="revenue"
              formatValue={money}
              color={GREEN}
              emptyHint="No TVOD purchases yet."
            />
            <GroupedBars
              title="Buy vs Rent by Title"
              rows={tvod.title_split}
              nameKey="title"
              formatValue={money}
              emptyHint="No TVOD purchases yet."
            />
            <BreakdownBars
              title="Most Bought Content"
              rows={insights.most_bought_content}
              nameKey="title"
              valueKey="purchases"
              formatValue={(v) => `${Number(v).toLocaleString()} buys`}
              color={COPPER}
              emptyHint="No purchases yet."
            />
          </div>
          {tvod.rentals &&
            (tvod.rentals.active > 0 || tvod.rentals.expiring_24h > 0 || tvod.rentals.expired > 0) && (
              <div className={styled.kpiRow}>
                <KpiCard label="Active Rentals" value={Number(tvod.rentals.active).toLocaleString()} accent={GREEN} />
                <KpiCard
                  label="Expiring in 24h"
                  value={Number(tvod.rentals.expiring_24h).toLocaleString()}
                  accent={COPPER}
                />
                <KpiCard label="Expired Rentals" value={Number(tvod.rentals.expired).toLocaleString()} accent={MAGENTA} />
              </div>
            )}
        </>
      )}

      {/* Creator monetization — exact figures from affiliate/payout/watch-ledger tables. */}
      {hasCreator && (
        <>
          <SectionHeader id="creators" accent={TEAL} title="Creator Monetization" />
          <div className={styled.kpiRow}>
            <KpiCard label="Commissions Earned" value={money(creator.total_commissions)} accent={GREEN} />
            <KpiCard label="Paid Out to Creators" value={money(creator.total_paid_out)} accent={TEAL} />
            <KpiCard label="Pending Payouts" value={money(creator.pending_payouts)} accent={COPPER} />
            <KpiCard
              label="Attributed Subscriptions"
              value={Number(creator.attributed_subscriptions).toLocaleString()}
              accent={MAGENTA}
            />
            <KpiCard label="Creator Watch Time" value={`${creator.total_watch_hours} h`} accent={TEAL} />
          </div>
          <div className={styled.chartsRow}>
            <BreakdownBars
              title="Top Earning Creators"
              rows={creator.top_creators}
              nameKey="creator"
              valueKey="commissions"
              formatValue={money}
              color={GREEN}
              emptyHint="No commissions yet."
            />
            <StackedBar
              title="Payout Status"
              rows={payoutStatus}
              formatValue={money}
              emptyHint="No payouts yet."
            />
            <BreakdownBars
              title="Most-Watched Content (by time)"
              rows={creator.top_content_by_watch}
              nameKey="title"
              valueKey="watch_minutes"
              formatValue={(v) => `${Number(v).toLocaleString()} min`}
              color={TEAL}
              emptyHint="No watch-time recorded yet."
            />
          </div>
          <div className={styled.chartsRow}>
            <BreakdownBars
              title="Top Creators by Watch-Time"
              rows={creator.top_creators_by_watch}
              nameKey="creator"
              valueKey="watch_minutes"
              formatValue={(v) => `${Number(v).toLocaleString()} min`}
              color={TEAL}
              emptyHint="No watch-time recorded yet."
            />
            <BreakdownBars
              title="Top Creators by TVOD Earnings"
              rows={creator.top_creators_by_tvod}
              nameKey="creator"
              valueKey="tvod_earnings"
              formatValue={money}
              color={COPPER}
              emptyHint="No TVOD sales attributed to a creator yet."
            />
            <LeaderboardTable
              title="Creator Leaderboard"
              rows={creator.creator_leaderboard}
              columns={[
                { key: "creator", label: "Creator" },
                { key: "watch_minutes", label: "Watch", align: "right", render: (v) => `${Number(v).toLocaleString()}m` },
                { key: "commissions", label: "Affiliate", align: "right", render: (v) => money(v) },
                { key: "tvod_earnings", label: "TVOD", align: "right", render: (v) => money(v) },
                { key: "attributed_subs", label: "Subs", align: "right" },
              ]}
              emptyHint="No creator activity yet."
            />
          </div>
        </>
      )}

      {/* Quality & reliability — gauges/tiles only shown when real samples exist. */}
      {hasQuality && (
        <>
          <SectionHeader id="quality" accent={MAGENTA} title="Playback Quality" />
          <div className={styled.chartsRow}>
            {reliability && reliability.starts > 0 && (
              <RadialGauge
                title="Playback Error Rate"
                value={reliability.error_rate_percent}
                max={Math.max(10, reliability.error_rate_percent)}
                color={MAGENTA}
                caption={`${reliability.errors} of ${reliability.starts} plays`}
              />
            )}
            {qoe && qoe.samples > 0 && (
              <RadialGauge
                title="Avg Buffer Ratio"
                value={qoe.avg_buffer_ratio}
                max={Math.max(10, qoe.avg_buffer_ratio)}
                color={COPPER}
                caption={`${qoe.samples} samples`}
              />
            )}
            {session && session.samples > 0 && (
              <div className={styled.chartCard}>
                <div className={styled.chartCardHead}>
                  <h3 className={styled.chartTitle}>Avg Stop Position</h3>
                  <span className={styled.asOfTag}>Live</span>
                </div>
                <div className={styled.bigStat}>
                  <span className={styled.bigStatValue}>{duration(session.avg_seconds)}</span>
                  <span className={styled.bigStatCaption}>
                    Avg point viewers reach before stopping · {session.samples} sessions
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <SectionHeader id="audience" accent={TEAL} title="Audience & Engagement" />
      {/* Audience & engagement breakdowns. */}
      <div className={styled.chartsRow}>
        <BreakdownDonut
          title="Device Mix"
          rows={lists.top_devices}
          nameKey="device"
          valueKey="count"
          formatValue={(v) => Number(v).toLocaleString()}
          emptyHint="No device data yet."
        />
        <BreakdownDonut
          title="New vs Returning"
          subtitle="New = here for the first time. Returning = been here before."
          rows={newVsReturning}
          nameKey="label"
          valueKey="value"
          formatValue={(v) => Number(v).toLocaleString()}
          emptyHint="No viewer data yet."
        />
        <FunnelChart
          title="Engagement Funnel"
          stages={funnelStages}
          formatValue={(v) => Number(v).toLocaleString()}
          emptyHint="No engagement data yet."
        />
      </div>

      <SectionHeader id="content" accent={COPPER} title="Content Performance" />
      {/* Content performance from real playback events. */}
      <div className={styled.chartsRow}>
        <RankedList
          title="Top Performing Titles"
          rows={(insights.content_performance || []).map((c) => ({
            label: c.title,
            value: c.starts,
            caption: `${Number(c.starts).toLocaleString()} plays · ${c.completion_percent}% completion`,
          }))}
          accent={COPPER}
          emptyHint="No playback recorded yet."
        />
        <BreakdownBars
          title="Trending Now · Last 7 Days"
          rows={insights.trending_now}
          nameKey="title"
          valueKey="plays_7d"
          formatValue={(v) => `${Number(v).toLocaleString()} plays`}
          emptyHint="No recent playback."
          color={TEAL}
          asOf={false}
        />
        <ListSection
          title="Top Searches"
          emptyHint="No searches yet."
          rows={(insights.top_searches || []).map((s) => ({
            label: s.keyword,
            value: `${Number(s.count).toLocaleString()} searches`,
          }))}
        />
      </div>

      {/* Library health + live activity feed. */}
      <div className={styled.chartsRow}>
        {library && (
          <BreakdownDonut
            title="Content Library"
            rows={[
              { label: "Published", value: library.published },
              { label: "Draft", value: library.draft },
            ].filter((d) => d.value > 0)}
            nameKey="label"
            valueKey="value"
            formatValue={(v) => Number(v).toLocaleString()}
            emptyHint="No content yet."
          />
        )}
        <ActivityFeed items={overview.activity} emptyHint="No recent activity." />
      </div>

      {/* Existing grouped metric cards — preserved in full. */}
      {groups.map((g) => (
        <section key={g.title} className={styled.group}>
          <div className={styled.groupHeader}>
            <span className={styled.groupDot} style={{ background: g.accent }} />
            <h2 className={styled.groupTitle}>{g.title}</h2>
          </div>
          <div className={styled.gridContainer}>
            {g.cards.map((item, i) => (
              <div key={i} className={styled.card} style={{ "--accent": g.accent }}>
                <div className={styled.numText}>{item.display}</div>
                <p className={styled.paraText}>{item.name}</p>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Existing breakdown tables — preserved in full. */}
      <div className={styled.listsGrid}>
        <ListSection
          title="Top Countries"
          emptyHint="No country data yet — populates as viewers stream."
          rows={(lists.top_countries || []).map((c) => ({
            label: c.country,
            value: `${Number(c.views).toLocaleString()} views`,
            sub: `${c.share_percent}%`,
          }))}
        />
        <ListSection
          title="Top Cities"
          emptyHint="No city data yet — populates as viewers stream."
          note="City totals can be lower than country totals — some IPs resolve to a country but not a city."
          rows={(lists.top_cities || []).map((c) => ({
            label: c.country ? `${c.city}, ${c.country}` : c.city,
            value: `${Number(c.views).toLocaleString()} views`,
            sub: `${c.share_percent}%`,
          }))}
        />
        <ListSection
          title="Revenue by Platform"
          emptyHint="No paid revenue by platform yet."
          rows={(lists.revenue_by_platform || []).map((p) => ({
            label: p.platform,
            value: money(p.mrr),
            sub: `${p.subscribers} subs`,
          }))}
        />
      </div>
    </div>
  );
};

export default CommonCard;
